import { useState, useEffect } from 'react';
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import AudioRecorder from '@/components/tools/AudioRecorder';
import TextEditor from '@/components/tools/TextEditor';
import BpmTapTool from '@/components/tools/BpmTapTool';
import InstrumentNotes from '@/components/tools/InstrumentNotes';
import ToneGenerator from '@/components/tools/ToneGenerator';
import GuitarTuner from '@/components/tools/GuitarTuner';
import LinkEditor from '@/components/tools/LinkEditor';
import InspirationEditor from '@/components/tools/InspirationEditor';
import Metronome from '@/components/tools/Metronome';
import DroneTool from '@/components/tools/DroneTool';
import FileImporter from '@/components/tools/FileImporter';
import { Mic, FileText, Activity, Guitar, Volume2, X, HelpCircle, Radio, ExternalLink, Disc3, Waves, Upload } from 'lucide-react';

const TOOLS = [
    { 
        id: 'audio', 
        label: 'Record Audio', 
        icon: Mic, 
        color: 'fragment-audio',
        hint: 'Capture a melody, chord progression, or vocal idea'
    },
    { 
        id: 'text', 
        label: 'Write Text', 
        icon: FileText, 
        color: 'fragment-text',
        hint: 'Write lyrics, notes, or thoughts about your song'
    },
    { 
        id: 'inspiration', 
        label: 'Reference', 
        icon: Disc3, 
        color: 'fragment-inspiration',
        hint: 'Add Spotify, YouTube, or SoundCloud links for inspiration'
    },
    { 
        id: 'metronome', 
        label: 'Metronome', 
        icon: Activity, 
        color: 'fragment-metadata',
        hint: 'Practice with a metronome and save your tempo'
    },
    { 
        id: 'drone', 
        label: 'Drone', 
        icon: Waves, 
        color: 'fragment-drone',
        hint: 'Play a sustained tone for pitch reference'
    },
    { 
        id: 'bpm', 
        label: 'Tap BPM', 
        icon: Activity, 
        color: 'fragment-metadata',
        hint: 'Tap along to find the tempo, or set key and feel'
    },
    { 
        id: 'instrument', 
        label: 'Instrument', 
        icon: Guitar, 
        color: 'fragment-instrument',
        hint: 'Note tuning, capo position, chords, or technique'
    },
    { 
        id: 'tone', 
        label: 'Tone Generator', 
        icon: Volume2, 
        color: 'fragment-tone',
        hint: 'Play a reference pitch for tuning or key finding'
    },
    { 
        id: 'tuner', 
        label: 'Guitar Tuner', 
        icon: Radio, 
        color: 'fragment-tuning',
        hint: 'Tune your guitar with real-time pitch detection'
    },
    { 
        id: 'link', 
        label: 'External Link', 
        icon: ExternalLink, 
        color: 'fragment-link',
        hint: 'Link to Dropbox, Drive, or external files'
    },
    { 
        id: 'import', 
        label: 'Import File', 
        icon: Upload, 
        color: 'fragment-text',
        hint: 'Import audio or text files from your device'
    },
];

const ONBOARDING_KEY = 'songwriter-toolbox-onboarding-seen';

export default function AddFragmentDrawer({ open, onOpenChange, songId, onFragmentAdded }) {
    const [activeTool, setActiveTool] = useState(null);
    const [showHints, setShowHints] = useState(false);

    // Check if user has seen onboarding
    useEffect(() => {
        if (open && !localStorage.getItem(ONBOARDING_KEY)) {
            setShowHints(true);
            localStorage.setItem(ONBOARDING_KEY, 'true');
        }
    }, [open]);

    const handleToolComplete = (fragmentId) => {
        setActiveTool(null);
        onOpenChange(false);
        if (onFragmentAdded && fragmentId) {
            onFragmentAdded(fragmentId);
        }
    };

    const renderTool = () => {
        switch (activeTool) {
            case 'audio':
                return <AudioRecorder songId={songId} onComplete={handleToolComplete} />;
            case 'text':
                return <TextEditor songId={songId} onComplete={handleToolComplete} />;
            case 'bpm':
                return <BpmTapTool songId={songId} onComplete={handleToolComplete} />;
            case 'instrument':
                return <InstrumentNotes songId={songId} onComplete={handleToolComplete} />;
            case 'tone':
                return <ToneGenerator songId={songId} onComplete={handleToolComplete} />;
            case 'tuner':
                return <GuitarTuner songId={songId} onComplete={handleToolComplete} />;
            case 'link':
                return <LinkEditor songId={songId} onComplete={handleToolComplete} />;
            case 'inspiration':
                return <InspirationEditor songId={songId} onComplete={handleToolComplete} />;
            case 'metronome':
                return <Metronome songId={songId} onComplete={handleToolComplete} />;
            case 'drone':
                return <DroneTool songId={songId} onComplete={handleToolComplete} />;
            case 'import':
                return <FileImporter songId={songId} onComplete={handleToolComplete} />;
            default:
                return null;
        }
    };

    return (
        <Drawer open={open} onOpenChange={onOpenChange}>
            <DrawerContent className="max-h-[90vh]">
                <DrawerHeader className="relative">
                    <DrawerTitle className="font-display text-xl text-center">
                        {activeTool ? TOOLS.find(t => t.id === activeTool)?.label : 'Add Fragment'}
                    </DrawerTitle>
                    {activeTool ? (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setActiveTool(null)}
                            className="absolute left-4 top-1/2 -translate-y-1/2 hover:scale-105 transition-transform"
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    ) : (
                        showHints && (
                            <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-muted-foreground"
                                                onClick={() => setShowHints(!showHints)}
                                            >
                                                <HelpCircle className="w-4 h-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Hover tools for hints</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        )
                    )}
                </DrawerHeader>

                <div className="px-4 pb-8 overflow-auto">
                    {activeTool ? (
                        <div className="animate-fade-in">
                            {renderTool()}
                        </div>
                    ) : (
                        <TooltipProvider delayDuration={300}>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-lg mx-auto">
                                {TOOLS.map((tool, index) => {
                                    const Icon = tool.icon;
                                    return (
                                        <Tooltip key={tool.id}>
                                            <TooltipTrigger asChild>
                                                <button
                                                    onClick={() => setActiveTool(tool.id)}
                                                    className={`flex flex-col items-center gap-3 p-5 rounded-xl border border-border bg-card hover:bg-muted/50 hover:border-primary/30 hover:scale-105 active:scale-95 transition-all duration-normal group animate-fade-in ${
                                                        showHints ? 'hint-pulse' : ''
                                                    }`}
                                                    style={{ animationDelay: `${index * 50}ms` }}
                                                >
                                                    <div
                                                        className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-normal group-hover:scale-110 group-hover:shadow-md"
                                                        style={{ backgroundColor: `hsl(var(--${tool.color}) / 0.15)` }}
                                                    >
                                                        <Icon
                                                            className="w-6 h-6"
                                                            style={{ color: `hsl(var(--${tool.color}))` }}
                                                        />
                                                    </div>
                                                    <span className="text-sm font-medium text-foreground">
                                                        {tool.label}
                                                    </span>
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent 
                                                side="bottom" 
                                                className="max-w-[200px] text-center tooltip-animate"
                                            >
                                                <p className="text-xs">{tool.hint}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    );
                                })}
                            </div>
                        </TooltipProvider>
                    )}
                </div>
            </DrawerContent>
        </Drawer>
    );
}